import { useApiQuery } from '@/api/main/query'
import { Roles } from '@/entities/roles'
import {
  fetchTraderFailure,
  fetchTraderStart,
  fetchTraderSuccess,
  TraderModel,
} from '@/entities/trader'
import { USER_ROLE_KEY } from '@/features/auth'
import { useToast } from '@/shared/hooks'
import { getStorageItem } from '@/shared/lib/storage/localStorage'
import { useEffect, useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'

export const useTraderData = () => {
  const dispatch = useDispatch()
  const { addToast } = useToast()
  const userRole = getStorageItem<Roles | null>(USER_ROLE_KEY, null)
  const shouldFetch = userRole === Roles.Trader

  // Мемоизированная версия addToast
  const addToastMemoized = useCallback(
    (title: string, message: string, type: 'error' | 'success' | 'default') => {
      addToast(title, message, type)
    },
    [addToast]
  )

  const {
    data: dataTrader,
    error: errorTrader,
    isLoading: isQueryLoading,
  } = useApiQuery<TraderModel>(['trader', userRole], '/v1/user/trader', undefined, {
    enabled: shouldFetch,
  })

  // Мемоизированная ошибка
  const traderError = useMemo(() => errorTrader, [errorTrader])

  // Эффект для старта загрузки
  useEffect(() => {
    if (!shouldFetch) return
    dispatch(fetchTraderStart())
  }, [dispatch, shouldFetch])

  // Эффект для обработки ошибок
  useEffect(() => {
    if (!shouldFetch || !traderError) return

    const alreadyHandled = sessionStorage.getItem('traderErrorHandled')
    if (alreadyHandled === traderError.message) return

    sessionStorage.setItem('trader-error-handled', traderError.message)
    dispatch(fetchTraderFailure(traderError.message))
    addToastMemoized('Ошибка!', traderError.message, 'error')
  }, [traderError, dispatch, shouldFetch, addToastMemoized])

  // Эффект для успешной загрузки
  useEffect(() => {
    if (!shouldFetch || !dataTrader) return

    sessionStorage.removeItem('trader-error-handled')
    dispatch(fetchTraderSuccess(dataTrader))
  }, [dataTrader, dispatch, shouldFetch])

  return {
    dataTrader,
    errorTrader: traderError,
    isTrader: shouldFetch,
    isLoading: shouldFetch && isQueryLoading,
  }
}
